import { useSettling } from '@/lib/refresh'

/**
 * A thin indeterminate bar across the top of the content area, shown while a
 * click is still working.
 *
 * This is the half of "nothing happens when I click" that per-button spinners
 * cannot reach. Most gateway writes only `handle.signal(...)` a Temporal
 * workflow and return `{"ok": true}` before Postgres has the row, so
 * `lib/refresh.ts` repaints on a burst. Between the button going idle and the
 * list actually changing there is a window — often a second, sometimes more —
 * where the screen is simply wrong and nothing on it admits that. The bar owns
 * that window.
 *
 * Indeterminate on purpose: there is no percentage to report. A fake progress
 * animation that fills to 90% and waits would be a lie about knowing how long
 * this takes.
 *
 * `useSettling()` is mutation-driven, so routine background polling (the
 * dashboard case panel refetches on an interval) does not flicker it. See the
 * hook for why that matters.
 */
export function GlobalActivityBar() {
  const settling = useSettling()

  return (
    // Always mounted, opacity-toggled: mounting on demand would restart the
    // keyframes mid-slide on every click and read as a stutter. `h-0.5` and
    // `pointer-events-none` keep it out of the layout and out of the way.
    <div
      aria-hidden={!settling}
      role="progressbar"
      aria-busy={settling}
      aria-label={settling ? 'Working' : undefined}
      className={`pointer-events-none h-0.5 w-full shrink-0 overflow-hidden transition-opacity duration-200 ${
        settling ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="h-full w-1/3 animate-activity-slide rounded-full bg-primary" />
    </div>
  )
}
